import { periodOptions, reportingIntervalOptions } from "@/shared/lib/staffing-shared"

import type { CalculatorFormValues } from "./types"

export type FormFieldName = keyof CalculatorFormValues

export type BaseFieldDefinition = {
  name: FormFieldName
  labelKey: string
  placeholderKey?: string
}

export type NumberFieldDefinition = BaseFieldDefinition & {
  kind: "number"
  min?: number
  max?: number
  step?: number
  inputMode?: "numeric" | "decimal"
  suffixKey?: string
  secondarySuffixKey?: string
}

export type SelectOptionDefinition = {
  labelKey: string
  value: string
  disabled?: boolean
  helperKey?: string
}

export type SelectFieldDefinition = BaseFieldDefinition & {
  kind: "select"
  options: ReadonlyArray<SelectOptionDefinition>
}

export type FormFieldDefinition = NumberFieldDefinition | SelectFieldDefinition

const FIELD_LABEL_KEYS: Record<FormFieldName, string> = {
  incomingContacts: "form.fields.incomingContacts.label",
  period: "form.fields.period.label",
  averageHandleTimeSeconds: "form.fields.averageHandleTimeSeconds.label",
  requiredServiceLevel: "form.fields.requiredServiceLevel.label",
  targetAnswerTimeSeconds: "form.fields.targetAnswerTimeSeconds.label",
  shrinkage: "form.fields.shrinkage.label",
  maxOccupancy: "form.fields.maxOccupancy.label",
  averagePatienceSeconds: "form.fields.averagePatienceSeconds.label",
  workingHoursPerWeek: "form.fields.workingHoursPerWeek.label",
  reportingInterval: "form.fields.reportingInterval.label",
}

export const PRIMARY_FIELDS: FormFieldDefinition[] = [
  {
    name: "incomingContacts",
    labelKey: FIELD_LABEL_KEYS.incomingContacts,
    placeholderKey: "form.placeholders.incomingContacts",
    kind: "number",
    min: 1,
    step: 1,
    inputMode: "numeric",
  },
  {
    name: "period",
    labelKey: FIELD_LABEL_KEYS.period,
    placeholderKey: "form.placeholders.period",
    kind: "select",
    options: periodOptions,
  },
  {
    name: "averageHandleTimeSeconds",
    labelKey: FIELD_LABEL_KEYS.averageHandleTimeSeconds,
    placeholderKey: "form.placeholders.averageHandleTimeSeconds",
    kind: "number",
    min: 1,
    step: 1,
    inputMode: "numeric",
    suffixKey: "form.suffixes.seconds",
  },
  {
    name: "requiredServiceLevel",
    labelKey: FIELD_LABEL_KEYS.requiredServiceLevel,
    placeholderKey: "form.placeholders.requiredServiceLevel",
    kind: "number",
    min: 0,
    max: 100,
    step: 0.1,
    inputMode: "decimal",
    suffixKey: "form.suffixes.percent",
    secondarySuffixKey: "form.suffixes.answeredIn",
  },
  {
    name: "targetAnswerTimeSeconds",
    labelKey: FIELD_LABEL_KEYS.targetAnswerTimeSeconds,
    placeholderKey: "form.placeholders.targetAnswerTimeSeconds",
    kind: "number",
    min: 1,
    step: 1,
    inputMode: "numeric",
    suffixKey: "form.suffixes.seconds",
  },
]

export const ADVANCED_FIELDS: FormFieldDefinition[] = [
  {
    name: "maxOccupancy",
    labelKey: FIELD_LABEL_KEYS.maxOccupancy,
    placeholderKey: "form.placeholders.maxOccupancy",
    kind: "number",
    min: 0,
    max: 100,
    step: 0.1,
    inputMode: "decimal",
    suffixKey: "form.suffixes.percent",
  },
  {
    name: "shrinkage",
    labelKey: FIELD_LABEL_KEYS.shrinkage,
    placeholderKey: "form.placeholders.shrinkage",
    kind: "number",
    min: 0,
    max: 100,
    step: 0.1,
    inputMode: "decimal",
    suffixKey: "form.suffixes.percent",
  },
  {
    name: "averagePatienceSeconds",
    labelKey: FIELD_LABEL_KEYS.averagePatienceSeconds,
    placeholderKey: "form.placeholders.averagePatienceSeconds",
    kind: "number",
    min: 1,
    step: 1,
    inputMode: "numeric",
    suffixKey: "form.suffixes.seconds",
  },
  {
    name: "workingHoursPerWeek",
    labelKey: FIELD_LABEL_KEYS.workingHoursPerWeek,
    placeholderKey: "form.placeholders.workingHoursPerWeek",
    kind: "number",
    min: 1,
    step: 0.1,
    inputMode: "decimal",
    suffixKey: "form.suffixes.hours",
  },
  {
    name: "reportingInterval",
    labelKey: FIELD_LABEL_KEYS.reportingInterval,
    placeholderKey: "form.placeholders.reportingInterval",
    kind: "select",
    options: reportingIntervalOptions,
  },
]
